// Verification queue client (plan §4 GET /verification/queue, spec J2). Verify and send back are
// task actions (POST /tasks/:taskId/actions/verify | send-back) sent with the version the queue
// showed, so a task that changed meanwhile answers 409 instead of being overwritten.
import type { Http } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  queueActionResultSchema,
  queuePageSchema,
  type QueueActionResult,
  type QueuePage,
} from './schemas/verification.ts';

export * from './schemas/verification.ts';

export interface QueueParams {
  readonly page?: number;
  readonly pageSize?: number;
}

export interface VerificationApi {
  queue(params?: QueueParams, signal?: AbortSignal): Promise<QueuePage>;
  /** Verify a Done task; the note is optional. */
  verify(taskId: string, version: number, note?: string): Promise<QueueActionResult>;
  /** Send a Done task back to In progress; the reason is required by the API. */
  sendBack(taskId: string, version: number, reason: string): Promise<QueueActionResult>;
}

function queuePath(params: QueueParams): string {
  const search = new URLSearchParams();
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query === '' ? '/verification/queue' : `/verification/queue?${query}`;
}

const actionPath = (taskId: string, action: 'verify' | 'send-back'): string =>
  `/tasks/${pathSegment(taskId)}/actions/${action}`;

export function createVerificationApi(http: Http): VerificationApi {
  return {
    queue: (params = {}, signal) =>
      http.json({ method: 'GET', path: queuePath(params), signal }, queuePageSchema),
    // async, so a refused task id rejects like any other failure instead of throwing.
    verify: async (taskId, version, note) =>
      http.json(
        {
          method: 'POST',
          path: actionPath(taskId, 'verify'),
          version,
          body: note === undefined || note === '' ? {} : { note },
        },
        queueActionResultSchema,
      ),
    sendBack: async (taskId, version, reason) =>
      http.json(
        { method: 'POST', path: actionPath(taskId, 'send-back'), version, body: { reason } },
        queueActionResultSchema,
      ),
  };
}
